import { useState, useMemo } from "react";
import { Box, Flex, Text } from "rebass";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useMutation } from "@apollo/client";
import countryList from "react-select-country-list";

import { IUserProfile } from "MVVM/@types";
import { UserBio } from "../Header/Components/UserBio";
import { USER_UPDATE, GET_USER_PROFILE } from "MVVM/GraphQL/user";
import { GamesPillSelector } from "MVVM/Components/GamesPillSelector";
import { SocialInputs } from "./SocialInputs";

interface EditProfileContentProps {
  onClose: () => void;
  user: IUserProfile;
}

export const EditProfileContent = ({
  onClose,
  user,
}: EditProfileContentProps) => {
  const [username, setUsername] = useState(user?.username || "");
  const [selectedPlayedGames, setSelectedPlayedGames] = useState<string[]>(
    (user?.playedGames as unknown as string[]) || [],
  );
  const [countryCode, setCountryCode] = useState(
    user?.profile?.profile?.countryCode || "",
  );
  const [socials, setSocials] = useState({
    instagram: user?.profile?.profile?.socials?.instagram || "",
    tiktok: user?.profile?.profile?.socials?.tiktok || "",
    twitch: user?.profile?.profile?.socials?.twitch || "",
    twitter: user?.profile?.profile?.socials?.twitter || "",
    youtube: user?.profile?.profile?.socials?.youtube || "",
  });

  const history = useHistory();

  const countryOptions = useMemo(() => countryList().getData(), []);

  const [updateUser, { loading: updateLoading }] = useMutation(USER_UPDATE, {
    onCompleted: (data) => {
      if (!data.userUpdate.errors?.length) {
        onClose();
      }
    },
    refetchQueries: [
      {
        query: GET_USER_PROFILE,
        variables: { username: user?.username || user._id },
      },
    ],
  });

  const changes = useMemo(() => {
    const originalPlayedGames =
      (user?.playedGames as unknown as string[]) || [];
    const originalSocials = user?.profile?.profile?.socials || {};

    const arraysEqual = (arr1: string[], arr2: string[]) => {
      if (arr1.length !== arr2.length) return false;
      return arr1.every((item) => arr2.includes(item));
    };

    return {
      playedGames: !arraysEqual(selectedPlayedGames, originalPlayedGames),
      username: username !== (user?.username || ""),
      countryCode: countryCode !== (user?.profile?.profile?.countryCode || ""),
      socials: Object.keys(socials).some(
        (key) =>
          socials[key as keyof typeof socials] !==
          (originalSocials[key as keyof typeof originalSocials] || ""),
      ),
    };
  }, [selectedPlayedGames, username, countryCode, socials, user]);

  const hasChanges = () => {
    return Object.values(changes).some((changed) => changed);
  };

  const handleSave = async () => {
    if (!hasChanges()) return;

    const input: {
      playedGames?: string[];
      username?: string;
      countryCode?: string;
      socials?: Array<{ platform: string; account: string }>;
    } = {};

    if (changes.playedGames) {
      input.playedGames = selectedPlayedGames;
    }

    if (changes.username) {
      input.username = username;
    }

    if (changes.countryCode) {
      input.countryCode = countryCode;
    }

    if (changes.socials) {
      input.socials = Object.entries(socials).map(([platform, account]) => ({
        platform: platform.toUpperCase(),
        account: account?.trim() || "",
      }));
    }

    await updateUser({
      variables: { input },
    });
  };

  const handleAvatarButtonClick = () => {
    history.push("/accountsettings");
  };

  const handleGameToggle = (gameKey: string) => {
    setSelectedPlayedGames((prev) => {
      if (prev.includes(gameKey)) {
        return prev.filter((key) => key !== gameKey);
      } else {
        return [...prev, gameKey];
      }
    });
  };

  const handleSocialChange = (platform: string, value: string) => {
    setSocials((prev) => ({
      ...prev,
      [platform]: value,
    }));
  };

  return (
    <ModalWrapper>
      <ModalHeader>
        <HeaderTitle>Edit Profile</HeaderTitle>
      </ModalHeader>

      <ModalContent>
        <Section>
          <AvatarContainer>
            <Avatar
              src={user?.avatarUrl || "/default-avatar.jpg"}
              alt="Avatar"
            />
            <Flex flexDirection={"column"}>
              <SectionLabel mb="2">Avatar</SectionLabel>
              <ReplacePhotoButton onClick={handleAvatarButtonClick}>
                Replace Photo
              </ReplacePhotoButton>
            </Flex>
          </AvatarContainer>
        </Section>

        <Section>
          <SectionLabel mb="2">Username</SectionLabel>
          <StyledInput
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter username"
            disabled={!!user?.username}
          />
        </Section>

        <Section>
          <SectionLabel mb="2">Country</SectionLabel>
          <CountrySelectWrapper>
            {countryCode && (
              <CountryFlag
                src={`${
                  process.env.REACT_APP_S3_CLIP_BUCKET
                }/static/web/flags/${countryCode.toLowerCase()}.png`}
                alt={countryCode}
              />
            )}
            <StyledSelect
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
            >
              <option value="">Select a country</option>
              {countryOptions.map((country) => (
                <option key={country.value} value={country.value}>
                  {country.label}
                </option>
              ))}
            </StyledSelect>
          </CountrySelectWrapper>
        </Section>

        <Section>
          <SectionLabel mb="2">Bio</SectionLabel>

          <UserBio user={user} />
        </Section>

        <Section>
          <SectionLabel mb="3">Social Profiles</SectionLabel>
          <SocialInputs socials={socials} onChange={handleSocialChange} />
        </Section>

        <Section>
          <SectionLabel mb="2">Played Games</SectionLabel>
          <GamesPillSelector
            selectedGames={selectedPlayedGames}
            onGameToggle={handleGameToggle}
          />
        </Section>

        <ActionButtons>
          <CancelButton onClick={onClose} disabled={updateLoading}>
            Cancel
          </CancelButton>
          <SaveButton
            onClick={handleSave}
            disabled={updateLoading || !hasChanges()}
          >
            {updateLoading ? "Saving..." : "Save"}
          </SaveButton>
        </ActionButtons>
      </ModalContent>
    </ModalWrapper>
  );
};

const ModalWrapper = styled(Box)`
  background: ${({ theme }) => theme.colors.navy300};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 12px;
  max-width: 700px;
  overflow-y: auto;
  width: 100%;
`;

const ModalHeader = styled(Flex)`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.navy50};
  justify-content: space-between;
  padding: 16px;
`;

const ModalContent = styled(Box)`
  padding: 24px;
`;

const HeaderTitle = styled(Text)`
  font-size: 16px;
  font-style: Bold;
  font-weight: 700;
  leading-trim: NONE;
  letter-spacing: 0%;
  line-height: 20px;
`;

const Section = styled(Box)`
  padding-bottom: 12px;
`;

const SectionLabel = styled(Box)`
  color: ${({ theme }) => theme.colors.shade300};
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
`;

const AvatarContainer = styled(Flex)`
  align-items: center;
  gap: 16px;
`;

const Avatar = styled.img`
  border-radius: 50%;
  height: 64px;
  object-fit: cover;
  width: 64px;
`;

const ReplacePhotoButton = styled.button`
  background: ${({ theme }) => theme.colors.navy50};
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  font-family: Barlow;
  font-size: 12px;
  font-weight: 500;
  padding: 8px 10px;
  width: fit-content;

  &:hover {
    background: ${({ theme }) => theme.colors.navy200};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.shade600};
    color: ${({ theme }) => theme.colors.shade400};
    cursor: not-allowed;
    opacity: 0.6;

    &:hover {
      background: ${({ theme }) => theme.colors.shade600};
    }
  }
`;

const StyledInput = styled.input`
  background: ${({ theme }) => theme.colors.navy400};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 6px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.shade100};
  font-family: Barlow;
  font-size: 14px;
  padding: 12px;
  width: 100%;

  &:focus {
    border-color: ${({ theme }) => theme.colors.envy};
    outline: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.shade400};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.shade600};
    color: ${({ theme }) => theme.colors.shade400};
    cursor: not-allowed;
    opacity: 0.6;

    &:focus {
      border-color: ${({ theme }) => theme.colors.navy50};
    }
  }
`;

const CountrySelectWrapper = styled(Box)`
  align-items: center;
  display: flex;
  position: relative;
`;

const CountryFlag = styled.img`
  border-radius: 2px;
  height: 16px;
  left: 12px;
  object-fit: cover;
  pointer-events: none;
  position: absolute;
  width: 24px;
  z-index: 1;
`;

const StyledSelect = styled.select`
  background: ${({ theme }) => theme.colors.navy400};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 6px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.shade100};
  cursor: pointer;
  font-family: Barlow;
  font-size: 14px;
  padding: 12px;
  padding-left: 44px;
  width: 100%;

  &:focus {
    border-color: ${({ theme }) => theme.colors.envy};
    outline: none;
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.shade600};
    color: ${({ theme }) => theme.colors.shade400};
    cursor: not-allowed;
    opacity: 0.6;

    &:focus {
      border-color: ${({ theme }) => theme.colors.navy50};
    }
  }
`;

const ActionButtons = styled(Flex)`
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
`;

const CancelButton = styled.button`
  background: ${({ theme }) => theme.colors.navy50};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.shade300};
  cursor: pointer;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  padding: 10px 20px;

  &:hover {
    background: ${({ theme }) => theme.colors.navy200};
    color: white;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;

    &:hover {
      background: ${({ theme }) => theme.colors.navy50};
      color: ${({ theme }) => theme.colors.shade300};
    }
  }
`;

const SaveButton = styled.button`
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  padding: 10px 20px;

  &:hover {
    background: ${({ theme }) => theme.colors.darkerEnvy};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;

    &:hover {
      background: ${({ theme }) => theme.colors.envy};
    }
  }
`;
